import { useEffect, useRef, useCallback } from "react"
import { request } from "react-rb/request"
import { useRequestReducer } from "hooks/use-requests"
import { useMounted } from "hooks/use-mounted"

function createURL(url, path, queryParams = {}) {
  let location = [url, path].filter(Boolean).join("")

  const queryString = new URLSearchParams()
  Object.entries(queryParams).forEach(pair => {
    queryString.set(...pair)
  })

  if (queryString.toString().length) {
    location = location + `?${queryString.toString()}`
  }

  return location
}

function createRequest({ url, method = "GET", dispatch }) {
  return async (options = {}) => {
    let fetchOptions = {
      credentials: "include",
      headers: options.headers || {},
      method,
      data: options.data
    }

    try {
      dispatch({ type: "FETCH" })
      const response = await request(
        createURL(url, options.path, options.queryParams),
        fetchOptions
      )

      dispatch({ type: "RESOLVE", payload: response })

      return response
    } catch (error) {
      dispatch({ type: "REJECT", payload: error })
    }
  }
}

export const createRequestObject = ({ url, dispatch = () => null }) => ({
  get: createRequest({ url, dispatch }),
  post: createRequest({ url, dispatch, method: "POST" }),
  put: createRequest({ url, dispatch, method: "PUT" }),
  delete: createRequest({ url, dispatch, method: "DELETE" })
})

/**
 * @deprecated See docs/concepts/frontend-data-fetching.md
 *
 * == Example
 * const [data, setData] = useState()
 * const [request, loading] = useFetch("https://kit.com")
 * const [request, loading, status, error] = useFetch("https://kit.com")
 *
 * useEffect(() => {
 *   const fetchData = async () => {
 *     const response = await request.get({ path: "/data" })
 *     setData(response)
 *   }
 *   fetchData()
 * }, [])
 *
 * @param {string} url
 */
function useFetch(url) {
  const [state, dispatch] = useRequestReducer({
    data: undefined
  })

  const mounted = useMounted()
  const first = useRef(true)

  const handleAction = useCallback(action => {
    if (mounted.current) dispatch(action)
    // `mounted` is a ref, so we don't need to pass it as a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { status, error } = state

  const ref = useRef(null)

  if (!ref.current) {
    ref.current = createRequestObject({ url, dispatch: handleAction })
  }

  useEffect(() => {
    // Because we want to ensure that `request` does not change if the URL
    // does not change, and because we initialize the `request` ref above,
    // we don't want to set it on the first render (the first time this
    // useEffect runs)
    if (!first.current) {
      ref.current = createRequestObject({ url, dispatch: handleAction })
    }

    first.current = false

    return () => {
      handleAction({ type: "DISCONNECT" })
    }
  }, [url, dispatch, handleAction])

  return [ref.current, status === "loading", status, error]
}

export { useFetch }
